import { useEffect } from 'react';

type Callback = () => void;

const callbackMap = new Map<Element, Callback>();
let observer: IntersectionObserver | null = null;

function createObserver(options?: IntersectionObserverInit) {
    if (typeof window === 'undefined') return null;
    if (!('IntersectionObserver' in window)) return null;

    return new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            const cb = callbackMap.get(entry.target);
            if (!cb) return;

            cb();
            callbackMap.delete(entry.target);
            observer?.unobserve(entry.target);
        });
    }, options);
}

function getObserver(options?: IntersectionObserverInit) {
    if (!observer) {
        observer = createObserver(options);
    }
    return observer;
}

const observeOnce = (el: Element, callback: Callback, options?: IntersectionObserverInit) => {
    const io = getObserver(options);
    if (!io) return;

    callbackMap.set(el, callback);
    io.observe(el);
};

const unobserve = (el: Element) => {
    callbackMap.delete(el);
    observer?.unobserve(el);

    if (callbackMap.size === 0) {
        observer?.disconnect();
        observer = null;
    }
};

export function useOnceInView(selector: string, callback: () => void) {
    function vhToPx(vh: number) {
        if (typeof window === 'undefined') return 0;
        return window.innerHeight * (vh / 100);
    }

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const el = document.querySelector(selector);
        if (!el) return;

        observeOnce(el, callback, {
            rootMargin: `0px 0px -${vhToPx(20)}px 0px`,
            threshold: 0,
        });

        return () => {
            unobserve(el);
        };
    }, [selector, callback]);
}
